import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import BillToLookupField, { type BillToValue } from '../../../components/BillToLookupField';
import LookupSearchField from '../../../components/tablemaint/LookupSearchField';
import LookupResultCard from '../../order-management/components/LookupResultCard';
import quoteService from '../../../services/quote.service';
import type { CustomerLookupRow, LocationLookupRow } from '../../order-management/fixtures';
import ordersService, {
  type ChangeTemplateCandidate,
  type ChangeTemplatePreviewRow,
} from '../../../services/orders.service';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../../hooks/useAdditionalFields';

interface CodeName {
  recno: number;
  name: string;
}

interface Props {
  isOpen: boolean;
  pronumber: number | string;
  /** Prefill from the current pro (V1 changetemplate_lookup seeds these). */
  initialBillTo: CodeName;
  initialCustomer: CodeName;
  moveType?: string;
  /** The pro's current template — always listed as a candidate, filters or not.
   *  Templates are edited in place, so re-applying the current one is how an
   *  order picks up those edits (e.g. a changed bill-to), and the edit is what
   *  makes it stop matching the pro-seeded search filters. */
  currentTemplate?: string | number;
  /** Set when the order is already delivered: pin + lock the delivery-location filter
   *  so only same-delivery-location templates are searchable (V1 lookup readonly). */
  lockedDelivery?: { code: number; name: string } | null;
  onClose: () => void;
  /** Called after a successful apply so the container refetches the order. */
  onApplied?: () => void;
}

const s = (v: unknown): string => (v == null ? '' : String(v));

/** Candidate grid columns — a focused slice of the Templates-page shape.
 *  The pro's own template is always in the list (see `currentTemplate`), so it's
 *  tagged: without the tag it's an ordinary-looking row and there's no cue that
 *  re-applying it is the way to pick up the template's edits. */
const makeColumns = (
  currentTemplate: string,
): ColumnDef<ChangeTemplateCandidate>[] => [
  {
    accessorKey: 'pronumber',
    header: 'Template #',
    // Opted out of registry titling: the registry knows `pronumber` as "Pro No.",
    // and this dialog is already headed by the ORDER's pro — labelling the
    // template's own number the same way would read as that pro. De-duplication
    // is unaffected, so the field is still not offered twice in the picker.
    meta: { registryTitle: false },
    cell: (c) => {
      const v = s(c.getValue());
      if (!currentTemplate || v !== currentTemplate) return v;
      return (
        <span className="inline-flex items-center gap-1.5">
          {v}
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
            current
          </span>
        </span>
      );
    },
  },
  { accessorKey: 'movetype', header: 'Move', cell: (c) => s(c.getValue()) },
  { accessorKey: 'ratezone', header: 'Rate Zone', cell: (c) => s(c.getValue()) },
  { accessorKey: 'size', header: 'Size', cell: (c) => s(c.getValue()) },
  { accessorKey: 'equiptype', header: 'Type', cell: (c) => s(c.getValue()) },
  { accessorKey: 'isdrop', header: 'Drop', cell: (c) => s(c.getValue()) },
  { accessorKey: 'delivery', header: 'Delivery', cell: (c) => s(c.getValue()) },
  { accessorKey: 'quotenumber', header: 'Quote #', cell: (c) => s(c.getValue()) },
  { accessorKey: 'csr', header: 'CSR', cell: (c) => s(c.getValue()) },
  { accessorKey: 'approvedby', header: 'Approved By', cell: (c) => s(c.getValue()) },
];

/** Namespaces this grid's saved layout AND its added-column selection. The two
 *  must agree: a saved layout referring to a column the selection no longer
 *  fetches would show a permanently blank column. */
const CANDIDATE_GRID_KEY = 'change-template-candidates';

/**
 * Change Template — Step 1 (find & pick). Ports V1 changetemplate_lookup +
 * changetemplate_results into a single searchable panel: Bill-To / Customer /
 * Delivery-Location pickers (prefilled from the current pro, editable) drive a
 * live-searching grid of matching approved templates. Picking a row advances to
 * the change-summary review (Step 2, wired next).
 */
const ChangeTemplateModal: React.FC<Props> = ({
  isOpen,
  pronumber,
  initialBillTo,
  initialCustomer,
  moveType,
  currentTemplate,
  lockedDelivery,
  onClose,
  onApplied,
}) => {
  const [step, setStep] = useState<'search' | 'review'>('search');
  const [billTo, setBillTo] = useState<BillToValue>({
    recno: initialBillTo.recno || 0,
    name: initialBillTo.name || '',
  });
  const [customer, setCustomer] = useState<CodeName>({
    recno: initialCustomer.recno || 0,
    name: initialCustomer.name || '',
  });
  const [deliverTo, setDeliverTo] = useState<CodeName>(
    lockedDelivery
      ? { recno: lockedDelivery.code, name: lockedDelivery.name }
      : { recno: 0, name: '' },
  );
  const [rows, setRows] = useState<ChangeTemplateCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [capped, setCapped] = useState(false);
  const [selected, setSelected] = useState<ChangeTemplateCandidate | null>(null);
  const [preview, setPreview] = useState<ChangeTemplatePreviewRow | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [checkedOptional, setCheckedOptional] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  // Synchronous re-entrancy guard — a double-click / rapid re-fire must not send a
  // second apply before React re-renders the disabled button (mirrors the shipped
  // leg-double-add FE guard; pairs with the server-side submitNonce idempotency).
  const savingRef = useRef(false);

  const canSearch = billTo.recno > 0 && customer.recno > 0;
  const currentTemplateStr = currentTemplate ? String(currentTemplate) : '';
  const baseColumns = useMemo(() => makeColumns(currentTemplateStr), [currentTemplateStr]);

  // Any orders-scope registry field can be added as a column here, exactly as on the
  // Templates board — same registry, same `templates` binding, so the fields resolve
  // against the TEMPLATE tables and the ones a template has no counterpart for are
  // never offered. The selection is an input to the candidate FETCH below, so it is
  // read before the search effect and the search waits on `ready`.
  const fieldSelection = useAdditionalFieldSelection(CANDIDATE_GRID_KEY, { binding: 'templates' });
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const { columns, availableFields } = useAdditionalFieldColumns<ChangeTemplateCandidate>(
    fieldSelection, { baseColumns, binding: 'templates' },
  );

  const hasFieldChanges =
    !!preview?.eligible &&
    (preview.diffs.length > 0 || preview.invoiceLineDiffs.length > 0);
  // Replacing the legs IS a change even when no field differs — a template that
  // adds a chassis split used to land as "No changes" with Save disabled.
  const hasChanges =
    hasFieldChanges || (!!preview?.eligible && preview.legChange?.kind === 'replaced');

  // One-line leg notice (bugfix/#34227): legs being replaced, or a template layout
  // NOT applied because the order has a driver. Same amber line as the warnings.
  const legNotice =
    preview?.eligible && preview.legChange ? (
      <div className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
        ⚠ {preview.legChange.message}
      </div>
    ) : null;

  const handleSave = () => {
    if (savingRef.current || !selected || !preview?.eligible || !hasChanges) return;
    savingRef.current = true;
    setSaving(true);
    setApplyError(null);
    // One idempotency key per Save click.
    const nonce =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `ct-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    ordersService
      .changeTemplateApply({
        template: selected.pronumber,
        pros: [pronumber],
        selectedOptional: Array.from(checkedOptional),
        submitNonce: nonce,
      })
      .then((res) => {
        const row = res.results?.[0];
        if (!row || !row.applied) {
          setApplyError(
            (row?.errors && row.errors.join(' ')) ||
              'Could not apply the template change.',
          );
          return;
        }
        if (row.userMessages && row.userMessages.length) {
          // Surface any post-save rule alerts (v1 %usermessage).
          window.alert(row.userMessages.join('\n'));
        }
        onApplied?.();
        onClose();
      })
      .catch((e: unknown) => {
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setApplyError(detail || 'Could not apply the template change.');
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
      });
  };

  // Live search once both bill-to + customer are set (V1 gates search on both).
  useEffect(() => {
    // Holding on `ready` keeps the first search from firing WITHOUT the saved added
    // columns and then again with them.
    if (!isOpen || !canSearch || !fieldSelection.ready) {
      setRows([]);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      ordersService
        .changeTemplateCandidates(
          {
            billto: billTo.recno,
            customer: customer.recno,
            movetype: moveType || undefined,
            deliverto: deliverTo.recno || undefined,
            current_template: currentTemplate ? String(currentTemplate) : undefined,
            additionalFields: fieldSelection.additionalFields,
          },
          ctrl.signal,
        )
        .then((res) => {
          setRows(res.rows);
          setCapped(res.capped);
        })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
    // `additionalFieldsKey` (not the array) so a re-render with an equal selection
    // doesn't re-run the search; adding or removing a column does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, canSearch, billTo.recno, customer.recno, deliverTo.recno, moveType, currentTemplate,
      fieldSelection.ready, additionalFieldsKey]);

  // On entering review, pull the per-pro change summary vs the picked template.
  useEffect(() => {
    if (step !== 'review' || !selected) return;
    const ctrl = new AbortController();
    setPreviewLoading(true);
    setPreview(null);
    setApplyError(null);
    ordersService
      .changeTemplatePreview(
        { template: selected.pronumber, pros: [pronumber] },
        ctrl.signal,
      )
      .then((res) => {
        const row = res.rows[0] ?? null;
        setPreview(row);
        if (row) {
          setCheckedOptional(
            new Set(row.diffs.filter((d) => d.optional).map((d) => d.field)),
          );
        }
      })
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
    return () => ctrl.abort();
  }, [step, selected, pronumber]);

  // Alt+C closes. Applying the template gets no chord — it rewrites the order
  // from the template, and the search→review flow exists to confirm that.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  // Don't let a backdrop/X close abandon an in-flight apply.
  const handleClose = () => {
    if (savingRef.current) return;
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim/40 p-4"
      onMouseDown={handleClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-[min(1040px,96vw)] max-h-[90vh] flex flex-col"
        role="dialog"
        aria-label="Change Template"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={`Change Template — Pro ${String(pronumber)}`}
          onClose={handleClose}
          dragHandleProps={dragHandleProps}
          className="shrink-0"
        />

        {step === 'search' ? (
          <div className="p-4 flex flex-col gap-4 min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <BillToLookupField value={billTo} onChange={setBillTo} required />
              <div>
                <LookupSearchField<CustomerLookupRow>
                  label="Customer"
                  required
                  displayValue={customer.name}
                  onType={(t) => setCustomer({ recno: 0, name: t })}
                  onSelect={(r) => setCustomer({ recno: r.customercode, name: r.custname })}
                  search={(q) => quoteService.lookupCustomer(q)}
                  renderItem={(r) => (
                    <LookupResultCard
                      name={r.custname}
                      code={r.customercode}
                      address1={r.address1}
                      address2={r.address2}
                      city={r.city}
                      state={r.state}
                      zip={r.zip}
                    />
                  )}
                  itemValue={(r) => String(r.customercode)}
                  itemLabel={(r) => r.custname}
                  placeholder="Search customers by name or #…"
                />
                <div className="mt-1.5 text-xs">
                  {customer.recno > 0 ? (
                    <span className="text-green-700 dark:text-green-400">#{customer.recno}</span>
                  ) : (
                    <span className="text-muted">No Customer selected</span>
                  )}
                </div>
              </div>
              <div>
                <LookupSearchField<LocationLookupRow>
                  label="Delivery Location"
                  disabled={!!lockedDelivery}
                  displayValue={deliverTo.name}
                  onType={(t) => setDeliverTo({ recno: 0, name: t })}
                  onSelect={(r) => setDeliverTo({ recno: r.locationcode, name: r.lname })}
                  search={(q) => quoteService.lookupLocation(q)}
                  renderItem={(r) => (
                    <LookupResultCard
                      name={r.lname}
                      code={r.locationcode}
                      city={r.city}
                      state={r.state}
                      zip={r.zip}
                    />
                  )}
                  itemValue={(r) => String(r.locationcode)}
                  itemLabel={(r) => r.lname}
                  placeholder="Optional — narrow by delivery location…"
                />
                <div className="mt-1.5 text-xs text-muted">
                  {lockedDelivery ? (
                    <span className="text-amber-600 dark:text-amber-400">
                      🔒 Locked to #{deliverTo.recno} — order already delivered; only
                      same-delivery-location templates are allowed.
                    </span>
                  ) : deliverTo.recno > 0 ? (
                    `#${deliverTo.recno}`
                  ) : (
                    'Any delivery location'
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-[320px] overflow-hidden flex flex-col">
              {!canSearch ? (
                <div className="flex-1 flex items-center justify-center text-sm text-slate-500 dark:text-gray-400">
                  Select a Bill To and Customer to find matching approved templates.
                </div>
              ) : (
                <>
                  <TanTable<ChangeTemplateCandidate>
                    data={rows}
                    columns={columns}
                    settingsKey={CANDIDATE_GRID_KEY}
                    availableFields={availableFields}
                    additionalFields={fieldSelection.additionalFields}
                    onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
                    loading={loading}
                    enableSearch
                    searchPlaceholder="Filter templates…"
                    enablePagination={false}
                    getRowId={(r) => String(r.pronumber)}
                    onRowClick={(r) => {
                      setSelected(r);
                      setStep('review');
                    }}
                  />
                  {capped && (
                    <div className="mt-1 text-[0.6875rem] text-amber-600 dark:text-amber-400">
                      Showing the first results — narrow the search to see more.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="flex items-center justify-between shrink-0">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-gray-100">
                Change Summary → Template <span>{s(selected?.pronumber)}</span>
              </h4>
              <button
                type="button"
                onClick={() => setStep('search')}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                ← Pick a different template
              </button>
            </div>

            {previewLoading ? (
              <div className="py-10 text-center text-sm text-slate-500 dark:text-gray-400">
                Loading change summary…
              </div>
            ) : !preview ? (
              <div className="py-10 text-center text-sm text-slate-500 dark:text-gray-400">
                No summary available.
              </div>
            ) : !preview.eligible ? (
              <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                <div className="font-semibold mb-1">This order cannot be changed to this template:</div>
                <ul className="list-disc ml-5">
                  {preview.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : !hasChanges ? (
              <>
                {legNotice}
                <div className="py-8 text-center text-sm text-slate-500 dark:text-gray-400">
                  No changes — this order already matches the template.
                </div>
              </>
            ) : (
              <>
                {legNotice}
                {preview.warnings.map((w, i) => (
                  <div
                    key={i}
                    className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                  >
                    ⚠ {w}
                  </div>
                ))}
                {hasFieldChanges && (
                  <div className="overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                    <table className="w-full text-[0.75rem]">
                      <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                        <tr>
                          <th className="text-left font-medium px-2 py-1.5">Field</th>
                          <th className="text-left font-medium px-2 py-1.5">Current</th>
                          <th className="text-left font-medium px-2 py-1.5">New (Template)</th>
                          <th className="text-center font-medium px-2 py-1.5 w-14">Apply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.diffs.map((d) => {
                          const checked = d.optional ? checkedOptional.has(d.field) : true;
                          return (
                            <tr key={d.field} className="border-t border-slate-100 dark:border-gray-700">
                              <td className="px-2 py-1 text-slate-700 dark:text-gray-200">{d.label}</td>
                              <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{d.old || '—'}</td>
                              <td className="px-2 py-1 text-slate-900 dark:text-gray-100">{d.new || '—'}</td>
                              <td className="px-2 py-1 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!d.optional}
                                  onChange={(e) =>
                                    setCheckedOptional((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(d.field);
                                      else next.delete(d.field);
                                      return next;
                                    })
                                  }
                                  aria-label={`Apply ${d.label}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                        {preview.invoiceLineDiffs.map((li) => (
                          <tr key={`li-${li.line}`} className="border-t border-slate-100 dark:border-gray-700">
                            <td className="px-2 py-1 text-slate-700 dark:text-gray-200">Invoice Line {li.line}</td>
                            <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{li.old || '—'}</td>
                            <td className="px-2 py-1 text-slate-900 dark:text-gray-100">{li.new || '—'}</td>
                            <td className="px-2 py-1 text-center">
                              <input type="checkbox" checked disabled aria-label={`Apply Invoice Line ${li.line}`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {applyError && (
              <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">
                {applyError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1 shrink-0">
              <button
                type="button"
                onClick={() => setStep('search')}
                disabled={saving}
                className="px-3 py-1.5 rounded border border-slate-300 dark:border-gray-600 text-sm hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !hasChanges}
                title={
                  !hasChanges
                    ? 'No applicable changes'
                    : 'Apply this template to the order'
                }
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Applying…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ChangeTemplateModal;
